import type { ListBuilderConfiguration } from '../types';

/**
 * Item Warehouse Embedded list configuration
 * Shows items in a specific warehouse
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_WAREHOUSE_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-items-warehouse-embedded',
			cacheTTL: 600000,
			name: 'All Items',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'image',
					width: 60,
					fields: [
						{
							id: 'item_image',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'primaryImageUrl', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								imageOptions: {
									size: 'small',
									enableViewer: true,
								},
							},
							displayStyle: 'image',
						},
					],
					headers: [{ id: 'image_header', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'item-info',
					label: 'Item',
					width: 250,
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'manufacturer', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sku-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId2', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Item' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
					],
					verticalAlignment: 'top',
				},
				{
					id: 'category',
					label: 'Category',
					width: 180,
					fields: [
						{
							id: 'category',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'category', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Category' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'category', objectTable: 'item' },
					],
					verticalAlignment: 'top',
				},
				{
					id: 'quantity',
					label: 'On Hand',
					width: 80,
					fields: [
						{
							id: 'quantity-value',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityOnHand', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h5', text: 'On Hand' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityOnHand',
							objectTable: 'item',
						},
					],
					verticalAlignment: 'top',
				},
			],
			description: 'Items in warehouse',
			defaultSortId: 'column-item-info-asc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'itemSearch',
			'itemCategories',
			'itemManufacturers',
			'itemStatus',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'externalId2', fieldLabel: 'SKU' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'subcategory', fieldLabel: 'Subcategory' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'quantityOnHand', fieldLabel: 'Quantity On Hand' },
				{ fieldName: 'isActive', fieldLabel: 'Active' },
				{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
				{ fieldName: 'weight', fieldLabel: 'Weight' },
				{ fieldName: 'isHazmat', fieldLabel: 'Hazmat' },
				{ fieldName: 'isRefrigerated', fieldLabel: 'Refrigerated' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
			],
			description: 'Sort items by name',
		},
		{
			id: 'category',
			name: 'Category',
			fields: [
				{ direction: 'ASC', fieldName: 'category', objectTable: 'item' },
				{ direction: 'ASC', fieldName: 'subcategory', objectTable: 'item' },
			],
			description: 'Sort by category then subcategory',
		},
		{
			id: 'stock',
			name: 'Stock Level',
			fields: [
				{ direction: 'DESC', fieldName: 'quantityOnHand', objectTable: 'item' },
			],
			description: 'Sort by quantity on hand',
		},
	],
};
